import styled from "styled-components";
import { themeColor } from "ui/theme";
import moment from "moment";
import { useState } from "react";
import { useAuth } from "context/AuthContext";
import { getAvatarUrl } from "utils";
import { userRoute } from "pages/user/user.routes";
import { useNavigate } from "react-router-dom";
import { UnreadBadge } from "./UnreadBadge";

export const ChatList = ({
  chatList = [],
  onClickMessage,
  activeConversation,
  currentUserId,
  handleAvatarClick,
}) => {
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const [, setLastMessageUpdate] = useState(0);

  const isTeacher = userRole === 2;

  const handleLastMessageUpdate = () => {
    setLastMessageUpdate(Date.now());
  };

  return (
    <ChatListContainer>
      <ChatListHeader>
        <ChatListTitle>Messages</ChatListTitle>
      </ChatListHeader>
      <ChatListMessages>
        {chatList?.map((msg) => (
          <ChatItem
            key={msg.id}
            style={{
              cursor: "pointer",
              backgroundColor:
                activeConversation?.id === msg.id
                  ? "rgba(0, 0, 0, 0.05)"
                  : "transparent",
            }}
          >
            <AvatarContainer>
              <ChatItemAvatar
                onClick={() => {
                  // const recipientId = Object.values(msg.participants).find(
                  //   (participant) => participant !== currentUserId,
                  // );

                  // handleAvatarClick(recipientId)

                  onClickMessage(msg);
                }}
              >
                {(isTeacher && msg.userData.profile_image) ||
                  (!isTeacher && msg.userData.teacher_profile_image) ? (
                  <img
                    src={`${getAvatarUrl(isTeacher ? msg?.userData?.profile_image : msg.userData.teacher_profile_image)}`}
                    alt={`${msg?.userData?.first_name} ${msg?.userData?.last_name}`}
                    width="100%"
                    height="100%"
                    style={{ borderRadius: "50%" }}
                  />
                ) : (
                  <ChatAvatarLetter>
                    {msg?.userData?.first_name?.[0] || "?"}
                  </ChatAvatarLetter>
                )}
              </ChatItemAvatar>
              <BadgeContainer >
                <UnreadBadge
                  msg={msg}
                  currentUserId={currentUserId}
                  onLastMessageUpdate={handleLastMessageUpdate}
                />
              </BadgeContainer>
            </AvatarContainer>
            <ChatItemContent onClick={() => onClickMessage(msg)}>
              <ChatItemHeader>
                <ChatItemName
                  onClick={() => {
                    // navigate(`${userRoute.classDetail}${msg.userData.teacher_user_id}`);
                    onClickMessage(msg)
                  }}
                >
                  {`${msg?.userData?.first_name} ${msg?.userData?.last_name?.[0]?.toUpperCase()}.`}
                </ChatItemName>
                <ChatItemTime>
                  {moment(
                    Number(
                      msg.last_message_from_user_1_at >
                        msg.last_message_from_user_2_at
                        ? msg.last_message_from_user_1_at
                        : msg.last_message_from_user_2_at,
                    ),
                  ).format("hh:mm A")}
                </ChatItemTime>
              </ChatItemHeader>
              <ChatItemRow>
                <ChatItemText>{msg.lastMessage}</ChatItemText>
              </ChatItemRow>
            </ChatItemContent>
          </ChatItem>
        ))}
      </ChatListMessages>
    </ChatListContainer>
  );
};

const ChatListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-grow: 1;
  min-height: 0;
  padding-bottom: 1rem;
`;

const ChatListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const ChatListTitle = styled.h2`
  font-size: 1.5rem;
  color: ${themeColor("txt")};
  font-weight: 600;
`;

const ChatListMessages = styled.div`
  padding-block: 1rem;
  padding-right: 0.5rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 8px;
    background-color: ${themeColor("bgNeutral")};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background-color: ${themeColor("buttonBgNeutral")};
  }

  @media (max-width: 1024px) {
    padding-block: 0rem;
    padding-right: 0rem;
    width: 100%;
  }
`;

const ChatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid ${themeColor("buttonBgNeutral")};
`;

const ChatItemAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${themeColor("buttonBgNeutral")};
`;

const ChatItemContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ChatItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChatItemName = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: ${themeColor("txt")};
`;

const ChatItemTime = styled.p`
  font-size: 0.875rem;
  color: ${themeColor("txtLight")};
`;

const ChatItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  max-width: 15rem;
`;

const ChatItemText = styled.p`
  font-size: 0.875rem;
  color: ${themeColor("txt")};
  text-overflow: ellipsis; 
  flex-wrap: wrap;
  flex-grow: 1;
  overflow: hidden;
  word-break: break-word;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const ChatAvatarLetter = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  height: 100%;
  font-size: 26px;
  color: ${themeColor("blue")};
  font-weight: bold;
  background-color: ${themeColor("lightBlue")};
`;

const AvatarContainer = styled.div`
  position: relative;
  width: 50px;
  height: 50px;
  border-radius: 50%;
`

const BadgeContainer = styled.div`
  position: absolute;
  top: 0px;
  right: -5px;
`